import { useCallback, useMemo } from 'react';

import { useActorRef, useSelector } from '@xstate/react';
import { type ContextFrom, type EventFrom, fromPromise, type SnapshotFrom } from 'xstate';

import { createFavoritesMachine } from '../../favorites-machine';
import {
  type FavoriteWithId,
  type FetchFavoritesOutput,
  type ToggleFavoriteInput,
  type ToggleFavoriteOutput,
} from '../../favorites-machine.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A hook to provide an interface to communicate with the favorites state machine.
 */
export const useFavorites = <Favorite extends FavoriteWithId>(
  params: UseFavoritesParams<Favorite>,
) => {
  const { fetchFavorites, toggleFavorite } = params;

  // ─── State Machine ───────────────────────────────────────────────────

  const favoritesMachine = useMemo(() => createFavoritesMachine<Favorite>('waiting'), []);

  const fetchFavoritesActor = useMemo(
    () =>
      fromPromise<FetchFavoritesOutput<Favorite>, FavoritesMachineContext<Favorite>>(
        async ({ input }) => fetchFavorites(input),
      ),
    [fetchFavorites],
  );

  const toggleFavoriteActor = useMemo(
    () =>
      fromPromise<ToggleFavoriteOutput, ToggleFavoriteInput<Favorite>>(async ({ input }) =>
        toggleFavorite(input.context, input.event),
      ),
    [toggleFavorite],
  );

  // ─── Machine Actor ───────────────────────────────────────────────────

  const actorRef = useActorRef(
    favoritesMachine.provide({
      actors: { fetchFavorites: fetchFavoritesActor, toggleFavorite: toggleFavoriteActor },
    }),
    { input: {} },
  );

  // ─── Context ─────────────────────────────────────────────────────────

  const favorites = useSelector(actorRef, (snapshot) => snapshot.context.favorites);

  const canFetchMoreFavorites = useSelector(
    actorRef,
    (snapshot) => snapshot.context.canFetchMoreFavorites,
  );

  const mostRecentFavorite = favorites[0];

  // ─── Flags ───────────────────────────────────────────────────────────

  const isFetchingInitialFavorites = useSelector(actorRef, checkIfFetchingInitialFavorites);
  const isReFetchingFavorites = useSelector(actorRef, checkIfReFetchingFavorites);
  const isFetchingMoreFavorites = useSelector(actorRef, checkIfFetchingMoreFavorites);
  const isTogglingFavoriteState = useSelector(actorRef, checkIfTogglingFavoriteState);

  // ─── Machine Helpers ─────────────────────────────────────────────────

  const fetchInitialFavorites = useCallback(() => {
    actorRef.send({ type: 'START' });
  }, [actorRef]);

  const fetchMoreFavorites = useCallback(() => {
    actorRef.send({ type: 'FETCH_MORE_FAVORITES' });
  }, [actorRef]);

  const refetchFavorites = useCallback(() => {
    actorRef.send({ type: 'REFETCH_FAVORITES' });
  }, [actorRef]);

  const toggleFavoriteLineItem = useCallback(
    (payload: ToggleFavoritePayload) => {
      const { id, productId, isFavorite } = payload;

      actorRef.send({ type: 'TOGGLE_FAVORITE', id, isFavorite, productId });
    },
    [actorRef],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    favorites,
    mostRecentFavorite,
    isFetchingInitialFavorites,
    isFetchingMoreFavorites,
    isReFetchingFavorites,
    isTogglingFavoriteState,
    canFetchMoreFavorites,
    fetchMoreFavorites,
    fetchInitialFavorites,
    refetchFavorites,
    toggleFavoriteLineItem,
  };
};

// ─── Selectors ───────────────────────────────────────────────────────────────

function checkIfFetchingInitialFavorites<Favorite extends FavoriteWithId>(
  snapshot: FavoritesMachineSnapshot<Favorite>,
) {
  return snapshot.matches('waiting') || snapshot.matches('fetchingInitialFavorites');
}

function checkIfReFetchingFavorites<Favorite extends FavoriteWithId>(
  snapshot: FavoritesMachineSnapshot<Favorite>,
) {
  return snapshot.matches('refetchingFavorites');
}

function checkIfFetchingMoreFavorites<Favorite extends FavoriteWithId>(
  snapshot: FavoritesMachineSnapshot<Favorite>,
) {
  return snapshot.matches('fetchingMoreFavorites');
}

function checkIfTogglingFavoriteState<Favorite extends FavoriteWithId>(
  snapshot: FavoritesMachineSnapshot<Favorite>,
) {
  return snapshot.matches('togglingFavoriteState');
}

// ─── Types ───────────────────────────────────────────────────────────────────

type FavoritesMachine<Favorite extends FavoriteWithId> = ReturnType<
  typeof createFavoritesMachine<Favorite>
>;

type FavoritesMachineSnapshot<Favorite extends FavoriteWithId> = SnapshotFrom<
  FavoritesMachine<Favorite>
>;

type UseFavoritesParams<Favorite extends FavoriteWithId> = {
  fetchFavorites: (
    context: FavoritesMachineContext<Favorite>,
  ) => Promise<FetchFavoritesOutput<Favorite>>;
  toggleFavorite: (
    context: FavoritesMachineContext<Favorite>,
    event: Extract<EventFrom<FavoritesMachine<Favorite>>, { type: 'TOGGLE_FAVORITE' }>,
  ) => Promise<ToggleFavoriteOutput>;
};

type FavoritesMachineContext<Favorite extends FavoriteWithId> = ContextFrom<
  FavoritesMachine<Favorite>
>;

type ToggleFavoritePayload = {
  id: string;
  productId: string;
  isFavorite: boolean;
};
